import { Button, Modal, Spin } from 'antd';
import React, {  ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { MarkerItem } from '../../../typings/home';
import {  IEnrichedMetadata, IGetMetadata } from '../../../typings/metadata';
import { StoreContext } from '../../../store/store';
import Api from '../../../api/Api';
import { featureCollection, point } from '@turf/helpers';
import nearestPoint from '@turf/nearest-point';
import TimeSeries from './ValidationPlot/TimeSeries';
import RosePlot from './ValidationPlot/RosePlot';
import ScatterPlot from './ValidationPlot/ScatterPlot';
import { IItemsValidation } from '../../../typings/analytics';
import moment, { Moment } from 'moment';
import { DownloadCheckboxKey, INSITU_TAB, ROSE_PLOT_VALIDATION, SCATTER_PLOT_VALIDATION, TS_PLOT_VALIDATION } from '../../../generic/common';
import { UserGroupPermissions } from '../../../typings/userGroupPermissions';
import { isEmpty, isEqual } from 'underscore';
import { useIsGlobalHDDtu, useSubscriptionStatus } from '../../../generic/hooks';
import NoInSituData from './NoInsituAvailable';
import { useData } from '../Store/DatasetContext';

interface Props {
  activePoint?: MarkerItem;
  validInSituPoint?: boolean
  onChangePointLocation: (coords: number[], forceChange?: boolean) => void
  isInsituAvailable: boolean
  activePanel: string
  userGroupPermissions: UserGroupPermissions;
  waterDepth: string
  cellSize: string
  datasetId: string
  headerSection: ReactNode
  activeInsituTab: number
  onMeshControl: (item: string) => void
  onChangeActiveKey: (item: any) => void
}

const InSituTab: React.FC<Props> = ({
    activePoint,
    validInSituPoint,
    onChangePointLocation,
    cellSize,
    waterDepth,
    activeInsituTab,
    activePanel,
    userGroupPermissions,
    datasetId,
    headerSection,
    onChangeActiveKey
}) => {
  const { dispatch } = useData()

    const [listParameters, setListParameters] = useState<IItemsValidation[]>([])
    const [enrichedMetadata, setEnrichedMetadata] = useState<IEnrichedMetadata[]>([])
    const [variable, setVariable] = useState<string | number>('')
    const [startDate, setStartDate] = useState<Moment | undefined>();
    const [endDate, setEndDate] = useState<Moment | undefined>();
    const [loading, setLoading] = useState(false)

    const context = useContext(StoreContext)
    const prevCoordinatesRef = useRef<number[]>();
    const prevDataset = useRef<string>();
    const prevMetadata = useRef<string>();
    const isDTU = useIsGlobalHDDtu(datasetId)

    const uploadRef = useRef<any>(null);

    const onDatasetChange = (value: string) => {
      dispatch({
        type: 'SET_TEMP_DATASET', payload: {
          pointId: activePoint?.id!,
          datasetId: datasetId,
          label: activePoint?.label!,
          name: value
        }
      });
      context.setIsChangeDatasetWarningVisible((prevState: { [key: string]: boolean }) => ({
        ...prevState,
        [category as string]: true
      }));
    }

    const handleChangeCategoryKey = useCallback((value: any) => {
      if(activePoint){
        context.setActiveCategoryKey((prevState: { [key: string]: boolean }) => ({
          ...prevState,
          [activePoint.id]: value
        }))
      }
    }, [activePoint])

    const category = useMemo(() => {
      return  activePoint && !isEmpty(context.activeCategoryKey) ? context.activeCategoryKey[activePoint.id] : DownloadCheckboxKey.SW;
    }, [context.activeCategoryKey, activePoint])
    const bestDataset = useMemo(() => {
      return activePoint?.downloadPage.find((dp) => dp.radioKey === category)?.options
    }, [category, activePoint])

    const activeInSituPoint = useMemo(() => {
        if (context.inSituPoints && context.inSituPoints.length > 0 && activePoint?.coordinates) {
            const from = point(activePoint.coordinates);
            const to = context.inSituPoints.map(
                (item) => point(item.position.slice(0, 2), {
                  source: item.source,
                  name: item.name,
                  startDate: item.startDate,
                  endDate: item.endDate,
                  type:item.type
                })
            );
            const features = featureCollection(to);
            const nearest = nearestPoint(from, features);
            if (nearest) {
                return {
                    name: activePoint?.insituPointName ?? (nearest.properties?.name || ''),
                    coordinates: nearest.geometry.coordinates as [number, number],
                    source: nearest.properties?.source,
                    dateRange: [moment(nearest.properties.startDate), moment(nearest.properties.endDate)] as [moment.Moment, moment.Moment],
                    type:  nearest.properties.type
                };
            }
        }
        return {
            name: '',
            source: '',
            coordinates: [],
            type: '',
        }
    }, [context.inSituPoints, activePoint?.coordinates])

    useEffect(() => {
        init()
    }, [validInSituPoint, userGroupPermissions, activePanel, cellSize, waterDepth])

    const init = async () => {
      try {
        if (activePoint && userGroupPermissions && validInSituPoint && activePanel === INSITU_TAB) {
          if(!isEqual(prevMetadata.current, datasetId)){
            prevMetadata.current = datasetId
            setLoading(true)
            const metadata = await Api.metadata.getMetadata(datasetId) as IGetMetadata
            if(listParameters.length === 0){
              const parametersData = await Api.insituPoint.getParametersInsitu();

              if(parametersData?.length > 0){
                const enrichedData = parametersData.map((item : IItemsValidation) => ({ ...item }));

                enrichedData.forEach((item : IItemsValidation) => {
                  const match = metadata?.ItemsInfo.find((compareItem) => compareItem.ShortName === item.name);
                  if (match) {
                    item.details = match.Name;
                    item.unit = match?.Unit ?? ''
                  }
                });

                setListParameters(enrichedData ?? [])
              }
              else {
                setListParameters([])
              }
            }
            if (!isEqual(prevCoordinatesRef.current, activePoint.coordinates) || !isEqual(prevDataset.current, datasetId)) {
              prevCoordinatesRef.current = activePoint.coordinates;
              prevDataset.current = datasetId
              context.setPreviewAnalytics([
                ...context.previewAnalytics,
                {
                  datasetId: metadata.DatasetId,
                  ItemsInfo: metadata.ItemsInfo,
                  DefaultDirectionShortName: metadata.DefaultDirectionShortName,
                  DefaultValueShortName: metadata.DefaultValueShortName,
                  coordinates: activePoint.coordinates,
                },
              ]);
              const createFullExtractionTasks = await Api.service
              .createFullExtractionTasks(
                [metadata],
                activePoint?.coordinates!,
                activePoint?.label!,
                cellSize,
                waterDepth,
                context.datasetBriefs,
                context.fwePoints,
                isDTU ? parseInt(context.timeStepInterval) : undefined
              );
              setEnrichedMetadata((prev) => [...prev, ...createFullExtractionTasks])
            }
          }
        }
      } catch (error) {
        console.error('Error fetching parameters:', error);
      } finally {
        setLoading(false)
      }
    }

    const onChangeMatchInsitu = () => {
        if (activePoint?.coordinates) {
            onChangePointLocation(
                [Number(activeInSituPoint.coordinates[0]), Number(activeInSituPoint.coordinates[1])]
            )
        }
    }

    const updateInSituPoints = async () => {
      try {
        setLoading(true)
        const insitu = await Api.insituPoint.getInsituPoints();
        context.setInSituPoints(insitu);
      } catch (error) {
        console.error('Error getting insitu point')
      } finally {
        setLoading(false)
      }
    };

    const notValidInsitu = (
      <NoInSituData
        disabledUpload={loading}
        latitude={activeInSituPoint.coordinates[0]}
        longitude={activeInSituPoint.coordinates[1]}
        name={activeInSituPoint.name}
        source={activeInSituPoint.source}
        onChangeToMatchInSitu={onChangeMatchInsitu}
        onUploadComplete={updateInSituPoints}
        uploadRef={uploadRef}
      />
    )

    return (
      <>
        <div style={{ display: !loading && activeInsituTab === TS_PLOT_VALIDATION ? 'block' : 'none', height: '100%' }}>
            {<TimeSeries
                variable={variable}
                setVariable={setVariable}
                endDate={endDate}
                setEndDate={setEndDate}
                startDate={startDate}
                setStartDate={setStartDate}
                activeCategoryKey={category as string}
                setActiveCategory={handleChangeCategoryKey}
                listParameters={listParameters}
                validInSituPoint={validInSituPoint && context.showInSituPoints}
                enrichedMetadata={enrichedMetadata}
                activePoint={activePoint}
                tooltipName={{
                    insitu: activeInSituPoint.name,
                    point: activePoint?.label
                }}
                activeInsituType={activeInSituPoint.type}
                dateRange={activeInSituPoint.dateRange}
                activeTab={activeInsituTab}
                activePanel={activePanel}
                datasetId={datasetId}
                userGroupPermissions={userGroupPermissions}
                bestDataset={bestDataset}
                timeStepInterval={context.timeStepInterval}
                headerSection={headerSection}
                datasetChange={onDatasetChange}
                notValidInsitu={notValidInsitu}
                onChangeActiveKey={onChangeActiveKey}
            />}
        </div>
        <div style={{ display: !loading && activeInsituTab === SCATTER_PLOT_VALIDATION ? 'block' : 'none', height: '100%' }}>
          <ScatterPlot
              variable={variable}
              setVariable={setVariable}
              endDate={endDate}
              setEndDate={setEndDate}
              startDate={startDate}
              setStartDate={setStartDate}
              activeCategoryKey={category as string}
              setActiveCategory={handleChangeCategoryKey}
              validInSituPoint={validInSituPoint && context.showInSituPoints}
              enrichedMetadata={enrichedMetadata}
              activePoint={activePoint}
              insituName={activeInSituPoint.name}
              listParameters={listParameters}
              activeTab={activeInsituTab}
              dateRange={activeInSituPoint.dateRange}
              datasetId={datasetId}
              bestDataset={bestDataset}
              userGroupPermissions={userGroupPermissions}
              activeInsituType={activeInSituPoint.type}
              cellSize={cellSize}
              waterDepth={waterDepth}
              headerSection={headerSection}
              datasetChange={onDatasetChange}
              notValidInsitu={notValidInsitu}
              onChangeActiveKey={onChangeActiveKey}

            />
        </div>
        <div style={{ display: !loading && activeInsituTab === ROSE_PLOT_VALIDATION ? 'block' : 'none', height: '100%' }}>
            <RosePlot
              activeCategoryKey={category as string}
              setActiveCategory={handleChangeCategoryKey}
              validInSituPoint={validInSituPoint && context.showInSituPoints}
              enrichedMetadata={enrichedMetadata}
              activePoint={activePoint}
              insituName={activeInSituPoint.name}
              listParameters={listParameters}
              dateRange={activeInSituPoint.dateRange}
              activeTab={activeInsituTab}
              datasetId={datasetId}
              userGroupPermissions={userGroupPermissions}
              bestDataset={bestDataset}
            />
        </div>
        {loading && (
          <Spin
            size='large'
            style={{
                display: 'flex',
                width: '100%',
                height: '100%',
                justifyContent: 'center',
                alignItems: 'center'
              }}
          />
        )}
      </>
    )
}

export default InSituTab;
